import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowRight, Route, ListChecks, Rocket, BookOpen, Clock, Users } from "lucide-react";
import { itinerarios } from "@/data/itinerarios";
import ItinerarioCard from "@/components/ItinerarioCard";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { usePageTracking, useTrackClick } from "@/hooks/useTracking";

const Cursos = () => {
  const navigate = useNavigate();
  const [difficultyFilter, setDifficultyFilter] = useState("todas");
  const trackClick = useTrackClick();
  usePageTracking();

  useSEO({
    title: "Guías IA Gratuitas para Pymes | Globaltis IA",
    description: `${itinerarios.length} itinerarios prácticos de IA. Aprende a implementar inteligencia artificial en tu negocio paso a paso. Gratis.`,
  });

  const totalArticles = useMemo(() =>
    itinerarios.reduce((acc, it) => acc + it.steps.reduce((a, s) => a + s.articleIds.length, 0), 0),
    []
  );

  const filteredItinerarios = useMemo(() => {
    if (difficultyFilter === "todas") return itinerarios;
    return itinerarios.filter(it => it.difficulty.toLowerCase() === difficultyFilter);
  }, [difficultyFilter]);

  const allSectors = useMemo(() => {
    const set = new Set<string>();
    itinerarios.forEach(it => it.targetAudience.forEach(t => set.add(t)));
    return Array.from(set).sort();
  }, []);

  const scrollToGuides = () => {
    document.getElementById("guias-grid")?.scrollIntoView({ behavior: "smooth" });
    trackClick("scroll_to_guides", "/cursos");
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">

      {/* 1. HERO */}
      <section className="pt-32 pb-24 px-4">
        <div className="max-w-5xl mx-auto text-center space-y-8">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-foreground tracking-tight leading-tight">
            Elige tu ruta. Aprende o delega.
          </h1>
          <p className="text-xl md:text-2xl font-light text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Rutas paso a paso — elige una y síguela en orden para implementar IA en tu negocio.
          </p>
          <p className="text-sm text-muted-foreground/70 font-light tracking-widest uppercase">
            {itinerarios.length} guías gratuitas · {totalArticles}+ artículos prácticos
          </p>
        </div>
      </section>

      {/* 2. CÓMO FUNCIONA */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-foreground text-center mb-16">
            Cómo funciona
          </h2>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              { icon: Route, title: "Elige tu ruta", desc: "Selecciona el itinerario que encaja con tu negocio y objetivo." },
              { icon: ListChecks, title: "Sigue los pasos", desc: "Cada guía tiene pasos claros con artículos y herramientas recomendadas." },
              { icon: Rocket, title: "Aplica en tu negocio", desc: "Implementa lo aprendido o contáctanos para que lo hagamos por ti." },
            ].map((item, i) => (
              <div key={i} className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <item.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-medium text-foreground">{item.title}</h3>
                <p className="text-muted-foreground font-light leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3-4. FILTRO + GRID */}
      <section id="guias-grid" className="py-20 px-4">
        <div className="max-w-7xl mx-auto space-y-10">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl font-light text-foreground">
              Todas las guías
            </h2>
            <Tabs defaultValue="todas" onValueChange={setDifficultyFilter} className="w-fit mx-auto">
              <TabsList>
                <TabsTrigger value="todas">Todas</TabsTrigger>
                <TabsTrigger value="fácil">Fácil</TabsTrigger>
                <TabsTrigger value="media">Media</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItinerarios.map(itinerario => (
              <ItinerarioCard key={itinerario.id} itinerario={itinerario} />
            ))}
          </div>
          {filteredItinerarios.length === 0 && (
            <p className="text-center text-muted-foreground py-12">No hay guías con esta dificultad.</p>
          )}
        </div>
      </section>

      {/* 5. SECTORES */}
      <section className="py-20 px-4 bg-secondary/30">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-light text-foreground">
            ¿Para quién son estas guías?
          </h2>
          <p className="text-lg text-muted-foreground font-light">
            Diseñadas para pymes, autónomos y equipos pequeños de cualquier sector.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {allSectors.map(sector => (
              <Badge key={sector} variant="secondary" className="text-sm px-4 py-2 font-normal">
                {sector}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* 6. SOCIAL PROOF */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto text-center space-y-12">
          <blockquote className="text-2xl md:text-3xl font-light text-foreground italic leading-relaxed max-w-3xl mx-auto">
            "Seguimos el itinerario de automatización y en 3 semanas ya habíamos recuperado 12 horas semanales de trabajo manual."
          </blockquote>
          <p className="text-muted-foreground font-light">— Responsable de operaciones, gestoría de 8 empleados</p>
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto pt-8">
            {[
              { value: "15h", label: "Ahorro semanal medio" },
              { value: "4 sem.", label: "Tiempo de implementación" },
              { value: "0 €", label: "Coste de las guías" },
            ].map((metric, i) => (
              <div key={i} className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-foreground">{metric.value}</div>
                <div className="text-sm text-muted-foreground font-light">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. CTA DUAL */}
      <section className="py-24 px-4 bg-secondary/30">
        <div className="max-w-3xl mx-auto text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-light text-foreground leading-tight">
            ¿Aprendes o delegas?
          </h2>
          <p className="text-lg text-muted-foreground font-light">
            Tú decides: sigue las guías a tu ritmo o déjalo en nuestras manos.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button size="lg" variant="outline" onClick={scrollToGuides} className="gap-2">
              <BookOpen className="h-4 w-4" />
              Empezar gratis
            </Button>
            <Button size="lg" onClick={() => { navigate("/contacto"); trackClick("cta_delega", "/cursos"); }} className="gap-2">
              Que lo haga Globaltis IA
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Cursos;
